import { FileText } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@f2e/ui/components/base"

import type { AIDraftFormProposal } from "@/hooks/api"

const displayValue = (value: unknown) => {
  if (value === undefined || value === null || value === "") return null
  return String(value)
}

/**
 * Read-only view of a proposed form inside a draft preview.
 *
 * No form exists at this point, so there is no submit or reject action here and
 * neither the schema nor the form's identity can be changed.
 */
export interface AIDraftFormProposalPreviewProps {
  /** Null when the draft proposed no form, so callers need no guard. */
  proposal: AIDraftFormProposal | null
}

export const AIDraftFormProposalPreview = ({
  proposal,
}: AIDraftFormProposalPreviewProps) => {
  const { t } = useTranslation()

  if (!proposal) return null

  return (
    <section
      className="mt-4 rounded-lg border border-dashed border-border/80 bg-muted/20 px-3 py-2.5"
      aria-label={t("history.aiDraft.form.previewLabel", {
        title: proposal.form_schema.title,
      })}
      data-testid="ai-draft-form-proposal-preview"
    >
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2">
          <FileText
            className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
          <div className="min-w-0">
            <div className="truncate text-sm font-medium">
              {proposal.form_schema.title}
            </div>
            {proposal.form_schema.description && (
              <div className="mt-0.5 text-xs text-muted-foreground">
                {proposal.form_schema.description}
              </div>
            )}
          </div>
        </div>
        <Badge variant="outline">{t("history.aiDraft.form.notSentYet")}</Badge>
      </header>

      <p className="mt-2 text-xs text-muted-foreground">
        {t("history.aiDraft.form.previewHelp")}
      </p>

      <dl className="mt-2 space-y-1.5">
        {proposal.form_schema.fields.map((field) => {
          const suggested = displayValue(proposal.prefilled_values[field.name])
          return (
            <div
              key={field.name}
              className="flex flex-wrap items-baseline gap-x-2 text-xs"
            >
              <dt className="font-medium text-foreground/80">
                {field.question || field.label || field.name}
                {field.required && (
                  <span className="ml-0.5 text-destructive" aria-hidden="true">
                    *
                  </span>
                )}
              </dt>
              <dd className="text-muted-foreground">
                {suggested ?? t("history.aiDraft.form.noSuggestedValue")}
              </dd>
            </div>
          )
        })}
      </dl>
    </section>
  )
}
